import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Progress } from "../components/ui/progress";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "../components/ui/select";
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";
import { cn } from "../lib/utils";
import { motion } from "framer-motion";
import { useLanguage } from "../components/LanguageProvider";

// Mock performance data
const performanceData = [
  { time: "00:00", cpu: 45, memory: 62, requests: 1200 },
  { time: "04:00", cpu: 38, memory: 58, requests: 800 },
  { time: "08:00", cpu: 72, memory: 75, requests: 3500 },
  { time: "12:00", cpu: 85, memory: 82, requests: 5200 },
  { time: "16:00", cpu: 68, memory: 78, requests: 4100 },
  { time: "20:00", cpu: 52, memory: 68, requests: 2400 },
  { time: "Now", cpu: 58, memory: 71, requests: 2800 },
];

const systemServices = [
  { id: 1, name: "API Gateway", status: "healthy", uptime: "99.98%", latency: "12ms", cpu: 45, memory: 62 },
  { id: 2, name: "Graph Engine", status: "healthy", uptime: "99.95%", latency: "28ms", cpu: 68, memory: 78 },
  { id: 3, name: "Search Index", status: "healthy", uptime: "99.99%", latency: "8ms", cpu: 35, memory: 55 },
  { id: 4, name: "Analytics Pipeline", status: "warning", uptime: "98.72%", latency: "156ms", cpu: 88, memory: 85 },
  { id: 5, name: "Storage Service", status: "healthy", uptime: "99.99%", latency: "15ms", cpu: 22, memory: 48 },
  { id: 6, name: "Auth Service", status: "healthy", uptime: "99.99%", latency: "5ms", cpu: 18, memory: 32 },
];

const recentLogs = [
  { timestamp: "12:45:32", level: "INFO", service: "API Gateway", message: "Request processed successfully" },
  { timestamp: "12:45:30", level: "WARN", service: "Analytics Pipeline", message: "High memory usage detected" },
  { timestamp: "12:45:28", level: "INFO", service: "Search Index", message: "Index refresh completed" },
  { timestamp: "12:45:25", level: "ERROR", service: "Graph Engine", message: "Connection timeout, retrying..." },
  { timestamp: "12:45:22", level: "INFO", service: "Storage Service", message: "Backup completed successfully" },
  { timestamp: "12:45:20", level: "DEBUG", service: "Auth Service", message: "Token validated for user JD-001" },
];

const logLevelColors = {
  INFO: "text-primary",
  WARN: "text-warning",
  ERROR: "text-destructive",
  DEBUG: "text-muted-foreground",
};

export default function AdminSettings() {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState("monitoring");

  return (
    <div className="p-3 lg:p-6 space-y-4 lg:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg lg:text-2xl font-bold text-foreground">{t("adminSettings")}</h2>
          <p className="text-xs lg:text-sm text-muted-foreground hidden sm:block">{t("systemConfig")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-8 text-xs lg:text-sm">
            <i className="fa-solid fa-rotate mr-1 lg:mr-2"></i>
            <span className="hidden sm:inline">{t("resetView")}</span>
          </Button>
          <Button size="sm" className="h-8 text-xs lg:text-sm">
            <i className="fa-solid fa-download mr-1 lg:mr-2"></i>
            <span className="hidden sm:inline">{t("export")} {t("logs")}</span>
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="overflow-x-auto">
          <TabsList className="w-full lg:w-auto">
            <TabsTrigger value="monitoring" className="gap-1 lg:gap-2 text-xs lg:text-sm flex-1 lg:flex-none">
              <i className="fa-solid fa-chart-line text-xs"></i>
              <span className="hidden sm:inline">{t("performance")}</span>
            </TabsTrigger>
            <TabsTrigger value="services" className="gap-1 lg:gap-2 text-xs lg:text-sm flex-1 lg:flex-none">
              <i className="fa-solid fa-server text-xs"></i>
              <span className="hidden sm:inline">{t("services")}</span>
            </TabsTrigger>
            <TabsTrigger value="logs" className="gap-1 lg:gap-2 text-xs lg:text-sm flex-1 lg:flex-none">
              <i className="fa-solid fa-file-lines text-xs"></i>
              <span className="hidden sm:inline">{t("logs")}</span>
            </TabsTrigger>
            <TabsTrigger value="settings" className="gap-1 lg:gap-2 text-xs lg:text-sm flex-1 lg:flex-none">
              <i className="fa-solid fa-gear text-xs"></i>
              <span className="hidden sm:inline">{t("settings")}</span>
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Performance Monitoring */}
        <TabsContent value="monitoring" className="space-y-4 lg:space-y-6 mt-4 lg:mt-6">
          {/* Real-time Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 lg:gap-4">
            <Card>
              <CardContent className="p-3 lg:p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs lg:text-sm text-muted-foreground">{t("cpuUsage")}</span>
                  <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30 text-[10px] lg:text-xs">{t("live")}</Badge>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-xl lg:text-3xl font-bold">58%</span>
                  <span className="text-[10px] lg:text-sm text-success">▼ 12%</span>
                </div>
                <Progress value={58} className="h-1.5 lg:h-2 mt-2 lg:mt-3" />
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 lg:p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs lg:text-sm text-muted-foreground">{t("memory")}</span>
                  <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30 text-[10px] lg:text-xs">{t("live")}</Badge>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-xl lg:text-3xl font-bold">71%</span>
                  <span className="text-[10px] lg:text-sm text-destructive">▲ 5%</span>
                </div>
                <Progress value={71} className="h-1.5 lg:h-2 mt-2 lg:mt-3" />
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 lg:p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs lg:text-sm text-muted-foreground">{t("requestsMin")}</span>
                  <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30 text-[10px] lg:text-xs">{t("live")}</Badge>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-xl lg:text-3xl font-bold">2.8K</span>
                  <span className="text-[10px] lg:text-sm text-success">▲ 15%</span>
                </div>
                <div className="mt-2 lg:mt-3 h-1.5 lg:h-2 bg-muted rounded-full overflow-hidden">
                  <div className="h-full w-3/4 bg-chart-2 rounded-full"></div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 lg:p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs lg:text-sm text-muted-foreground">{t("avgLatency")}</span>
                  <Badge variant="outline" className="bg-success/10 text-success border-success/30 text-[10px] lg:text-xs">{t("healthy")}</Badge>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-xl lg:text-3xl font-bold">24ms</span>
                  <span className="text-[10px] lg:text-sm text-success">▼ 8ms</span>
                </div>
                <div className="mt-2 lg:mt-3 h-1.5 lg:h-2 bg-muted rounded-full overflow-hidden">
                  <div className="h-full w-1/4 bg-success rounded-full"></div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <div className="grid gap-4 lg:gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader className="p-3 lg:p-6 pb-2">
                <CardTitle className="text-sm lg:text-base">{t("resourceUsage")}</CardTitle>
                <CardDescription className="text-xs hidden sm:block">{t("cpuMemoryUtilization")}</CardDescription>
              </CardHeader>
              <CardContent className="p-2 lg:p-6 pt-0">
                <div className="h-48 lg:h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={performanceData}>
                      <defs>
                        <linearGradient id="cpuGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="hsl(var(--chart-1))" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="memGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="hsl(var(--chart-2))" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="hsl(var(--chart-2))" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="time" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                      <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} unit="%" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "hsl(var(--card))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: "var(--radius)",
                          fontSize: "12px"
                        }}
                      />
                      <Area type="monotone" dataKey="cpu" stroke="hsl(var(--chart-1))" fill="url(#cpuGradient)" strokeWidth={2} name="CPU" />
                      <Area type="monotone" dataKey="memory" stroke="hsl(var(--chart-2))" fill="url(#memGradient)" strokeWidth={2} name={t("memory")} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="p-3 lg:p-6 pb-2">
                <CardTitle className="text-sm lg:text-base">{t("requestVolume")}</CardTitle>
                <CardDescription className="text-xs hidden sm:block">{t("apiRequestsPerHour")}</CardDescription>
              </CardHeader>
              <CardContent className="p-2 lg:p-6 pt-0">
                <div className="h-48 lg:h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={performanceData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="time" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                      <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "hsl(var(--card))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: "var(--radius)",
                          fontSize: "12px"
                        }}
                      />
                      <Bar dataKey="requests" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} name="Requests" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Services */}
        <TabsContent value="services" className="mt-4 lg:mt-6">
          <Card>
            <CardHeader className="p-3 lg:p-6">
              <CardTitle className="text-sm lg:text-base">{t("systemServices")}</CardTitle>
              <CardDescription className="text-xs lg:text-sm">{t("healthStatus")}</CardDescription>
            </CardHeader>
            <CardContent className="p-3 lg:p-6 pt-0">
              <div className="space-y-3 lg:space-y-4">
                {systemServices.map((service, index) => (
                  <motion.div
                    key={service.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-3 lg:p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors gap-3"
                  >
                    <div className="flex items-center gap-3 lg:gap-4">
                      <div className={cn(
                        "h-2.5 w-2.5 lg:h-3 lg:w-3 rounded-full relative",
                        service.status === "healthy" ? "bg-success" : "bg-warning"
                      )}>
                        {service.status === "warning" && (
                          <span className="absolute inset-0 rounded-full animate-ping bg-warning/50"></span>
                        )}
                      </div>
                      <div>
                        <h4 className="font-medium text-xs lg:text-sm">{service.name}</h4>
                        <p className="text-[10px] lg:text-sm text-muted-foreground">{t("uptime")}: {service.uptime}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 lg:gap-8 ml-6 sm:ml-0">
                      <div className="text-center hidden sm:block">
                        <p className="text-xs lg:text-sm font-medium">{service.latency}</p>
                        <p className="text-[10px] lg:text-xs text-muted-foreground">{t("latency")}</p>
                      </div>
                      <div className="w-16 lg:w-24">
                        <div className="flex items-center justify-between text-[10px] lg:text-xs mb-1">
                          <span>CPU</span>
                          <span>{service.cpu}%</span>
                        </div>
                        <Progress 
                          value={service.cpu} 
                          className={cn("h-1 lg:h-1.5", service.cpu > 80 && "[&>div]:bg-warning")} 
                        />
                      </div>
                      <div className="w-16 lg:w-24">
                        <div className="flex items-center justify-between text-[10px] lg:text-xs mb-1">
                          <span>{t("memory")}</span>
                          <span>{service.memory}%</span>
                        </div>
                        <Progress 
                          value={service.memory} 
                          className={cn("h-1 lg:h-1.5", service.memory > 80 && "[&>div]:bg-warning")} 
                        />
                      </div>
                      <Badge 
                        variant="outline"
                        className={cn(
                          "capitalize text-[10px] lg:text-xs",
                          service.status === "healthy" && "bg-success/10 text-success border-success/30",
                          service.status === "warning" && "bg-warning/10 text-warning border-warning/30"
                        )}
                      >
                        {t(service.status) || service.status}
                      </Badge>
                      <Button variant="ghost" size="icon" className="h-6 w-6 lg:h-8 lg:w-8 hidden sm:flex">
                        <i className="fa-solid fa-ellipsis-vertical text-xs"></i>
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Logs */}
        <TabsContent value="logs" className="mt-4 lg:mt-6">
          <Card>
            <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between p-3 lg:p-6 gap-3">
              <div>
                <CardTitle className="text-sm lg:text-base">{t("systemLogs")}</CardTitle>
                <CardDescription className="text-xs lg:text-sm hidden sm:block">{t("realTimeAppLogs")}</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Select defaultValue="all">
                  <SelectTrigger className="w-24 lg:w-32 h-8 text-xs lg:text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("allLevels")}</SelectItem>
                    <SelectItem value="error">{t("error")}</SelectItem>
                    <SelectItem value="warn">{t("warning")}</SelectItem>
                    <SelectItem value="info">{t("info")}</SelectItem>
                    <SelectItem value="debug">{t("debug")}</SelectItem>
                  </SelectContent>
                </Select>
                <Select defaultValue="all-services">
                  <SelectTrigger className="w-28 lg:w-40 h-8 text-xs lg:text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all-services">{t("allServices")}</SelectItem>
                    {systemServices.map(s => (
                      <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="p-3 lg:p-6 pt-0">
              <ScrollArea className="h-72 lg:h-96">
                <div className="font-mono text-[10px] lg:text-sm space-y-1">
                  {[...recentLogs, ...recentLogs, ...recentLogs].map((log, index) => (
                    <div 
                      key={index}
                      className="flex items-start gap-2 lg:gap-4 p-1.5 lg:p-2 rounded hover:bg-muted/50"
                    >
                      <span className="text-muted-foreground shrink-0">{log.timestamp}</span>
                      <Badge 
                        variant="outline" 
                        className={cn("shrink-0 w-12 lg:w-16 justify-center text-[9px] lg:text-xs", logLevelColors[log.level])}
                      >
                        {log.level}
                      </Badge>
                      <span className="text-primary shrink-0 w-20 lg:w-32 truncate hidden sm:block">[{log.service}]</span>
                      <span className="text-foreground truncate">{log.message}</span>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings */}
        <TabsContent value="settings" className="mt-4 lg:mt-6">
          <div className="grid gap-4 lg:gap-6 md:grid-cols-2">
            <Card>
              <CardHeader className="p-3 lg:p-6">
                <CardTitle className="text-sm lg:text-base">General Settings</CardTitle>
                <CardDescription className="text-xs lg:text-sm">Basic system configuration</CardDescription>
              </CardHeader>
              <CardContent className="p-3 lg:p-6 pt-0 space-y-4 lg:space-y-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-xs lg:text-sm">Maintenance Mode</Label>
                    <p className="text-[10px] lg:text-sm text-muted-foreground">Disable system access for users</p>
                  </div>
                  <Switch />
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-xs lg:text-sm">Debug Logging</Label>
                    <p className="text-[10px] lg:text-sm text-muted-foreground">Enable verbose logging</p>
                  </div>
                  <Switch />
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-xs lg:text-sm">Auto-backup</Label>
                    <p className="text-[10px] lg:text-sm text-muted-foreground">Daily automatic backups</p>
                  </div>
                  <Switch defaultChecked />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="p-3 lg:p-6">
                <CardTitle className="text-sm lg:text-base">Performance Tuning</CardTitle>
                <CardDescription className="text-xs lg:text-sm">Optimize system performance</CardDescription>
              </CardHeader>
              <CardContent className="p-3 lg:p-6 pt-0 space-y-4 lg:space-y-6">
                <div>
                  <Label className="text-xs lg:text-sm">Cache TTL</Label>
                  <Select defaultValue="1h">
                    <SelectTrigger className="mt-2 h-8 lg:h-9 text-xs lg:text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="15m">15 minutes</SelectItem>
                      <SelectItem value="1h">1 hour</SelectItem>
                      <SelectItem value="6h">6 hours</SelectItem>
                      <SelectItem value="24h">24 hours</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs lg:text-sm">Query Timeout</Label>
                  <Select defaultValue="30s">
                    <SelectTrigger className="mt-2 h-8 lg:h-9 text-xs lg:text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10s">10 seconds</SelectItem>
                      <SelectItem value="30s">30 seconds</SelectItem>
                      <SelectItem value="60s">60 seconds</SelectItem>
                      <SelectItem value="120s">2 minutes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs lg:text-sm">Max Concurrent Requests</Label>
                  <Input type="number" defaultValue="1000" className="mt-2 h-8 lg:h-9 text-xs lg:text-sm" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="p-3 lg:p-6">
                <CardTitle className="text-sm lg:text-base">Security</CardTitle>
                <CardDescription className="text-xs lg:text-sm">Security and access controls</CardDescription>
              </CardHeader>
              <CardContent className="p-3 lg:p-6 pt-0 space-y-4 lg:space-y-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-xs lg:text-sm">Two-Factor Auth</Label>
                    <p className="text-[10px] lg:text-sm text-muted-foreground">Require 2FA for all users</p>
                  </div>
                  <Switch defaultChecked />
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-xs lg:text-sm">IP Whitelisting</Label>
                    <p className="text-[10px] lg:text-sm text-muted-foreground">Restrict access by IP</p>
                  </div>
                  <Switch />
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-xs lg:text-sm">Audit Logging</Label>
                    <p className="text-[10px] lg:text-sm text-muted-foreground">Log all user actions</p>
                  </div>
                  <Switch defaultChecked />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="p-3 lg:p-6">
                <CardTitle className="text-sm lg:text-base">Integrations</CardTitle>
                <CardDescription className="text-xs lg:text-sm">External service connections</CardDescription>
              </CardHeader>
              <CardContent className="p-3 lg:p-6 pt-0 space-y-3 lg:space-y-4">
                <div className="flex items-center justify-between p-2 lg:p-3 rounded-lg border border-border">
                  <div className="flex items-center gap-2 lg:gap-3">
                    <div className="flex h-8 w-8 lg:h-10 lg:w-10 items-center justify-center rounded-lg bg-primary/10">
                      <i className="fa-solid fa-database text-primary text-xs lg:text-sm"></i>
                    </div>
                    <div>
                      <p className="font-medium text-xs lg:text-sm">Database</p>
                      <p className="text-[10px] lg:text-xs text-muted-foreground">PostgreSQL</p>
                    </div>
                  </div>
                  <Badge variant="outline" className="bg-success/10 text-success border-success/30 text-[10px] lg:text-xs">Connected</Badge>
                </div>
                <div className="flex items-center justify-between p-2 lg:p-3 rounded-lg border border-border">
                  <div className="flex items-center gap-2 lg:gap-3">
                    <div className="flex h-8 w-8 lg:h-10 lg:w-10 items-center justify-center rounded-lg bg-chart-2/10">
                      <i className="fa-solid fa-magnifying-glass text-chart-2 text-xs lg:text-sm"></i>
                    </div>
                    <div>
                      <p className="font-medium text-xs lg:text-sm">Search Engine</p>
                      <p className="text-[10px] lg:text-xs text-muted-foreground">Elasticsearch</p>
                    </div>
                  </div>
                  <Badge variant="outline" className="bg-success/10 text-success border-success/30 text-[10px] lg:text-xs">Connected</Badge>
                </div>
                <div className="flex items-center justify-between p-2 lg:p-3 rounded-lg border border-border">
                  <div className="flex items-center gap-2 lg:gap-3">
                    <div className="flex h-8 w-8 lg:h-10 lg:w-10 items-center justify-center rounded-lg bg-chart-3/10">
                      <i className="fa-solid fa-cloud text-chart-3 text-xs lg:text-sm"></i>
                    </div>
                    <div>
                      <p className="font-medium text-xs lg:text-sm">Object Storage</p>
                      <p className="text-[10px] lg:text-xs text-muted-foreground">AWS S3</p>
                    </div>
                  </div>
                  <Badge variant="outline" className="bg-success/10 text-success border-success/30 text-[10px] lg:text-xs">Connected</Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
